import { useEffect, useState } from "react";
import { Plus, Trash2, TrendingUp, ThumbsUp, Minus, ThumbsDown, Users } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const INTEREST = [
  { key: "yes", label: "Interested", icon: ThumbsUp, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  { key: "maybe", label: "Maybe", icon: Minus, cls: "text-amber-600 bg-amber-50 border-amber-200" },
  { key: "no", label: "Not now", icon: ThumbsDown, cls: "text-slate-500 bg-slate-50 border-slate-200" },
];

export default function ValidationTracker() {
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState("");
  const [contact, setContact] = useState("");
  const [interested, setInterested] = useState("yes");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/validation").then(({ data }) => setData(data)).catch(() => setData({ responses: [], summary: { yes: 0, maybe: 0, no: 0, total: 0, recommendation: "" } }));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!channel.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/validation/responses", { channel, contact, interested, note });
      setData(data);
      setChannel(""); setContact(""); setNote(""); setInterested("yes");
    } catch { toast.error("Couldn't save that response."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { const { data } = await api.delete(`/validation/responses/${id}`); setData(data); }
    catch { toast.error("Delete failed."); }
  };

  if (!data) return null;
  const s = data.summary;

  return (
    <div className="mt-8 rounded-2xl border border-brand-line bg-white p-5 shadow-soft" data-testid="validation-tracker">
      <div className="flex items-center gap-2">
        <Users size={16} className="text-brand" />
        <h3 className="font-heading text-base font-semibold">Validation tracker</h3>
      </div>
      <p className="mt-1 text-sm text-slate-500">Log what real people said. Your evidence summary updates as you go, so your decision is based on proof, not a hunch.</p>

      {/* Evidence summary */}
      <div className="mt-4 grid grid-cols-3 gap-2" data-testid="evidence-summary">
        {[
          { k: "yes", label: "Interested", val: s.yes, cls: "text-emerald-600" },
          { k: "maybe", label: "Maybe", val: s.maybe, cls: "text-amber-600" },
          { k: "no", label: "Not now", val: s.no, cls: "text-slate-500" },
        ].map((c) => (
          <div key={c.k} className="rounded-xl border border-brand-line bg-brand-surface/30 p-3 text-center">
            <p className={`font-heading text-2xl font-bold ${c.cls}`} data-testid={`evidence-${c.k}`}>{c.val}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>
      {s.total > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-brand-surface/50 p-3 text-sm text-brand-text" data-testid="evidence-recommendation">
          <TrendingUp size={15} className="mt-0.5 shrink-0" /> {s.recommendation}
        </div>
      )}

      {/* Add response */}
      <form onSubmit={add} className="mt-4 space-y-2">
        <div className="flex flex-wrap gap-2">
          <input data-testid="vr-channel" value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="Who / where (e.g. DM to Sara)"
            className="min-w-[180px] flex-1 rounded-lg border border-brand-line px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          <input data-testid="vr-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact (optional)"
            className="w-40 rounded-lg border border-brand-line px-3 py-2 text-sm focus:border-brand focus:outline-none" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {INTEREST.map((i) => (
            <button type="button" key={i.key} data-testid={`vr-interest-${i.key}`} onClick={() => setInterested(i.key)}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${interested === i.key ? i.cls : "border-brand-line bg-white text-slate-500"}`}>
              <i.icon size={13} /> {i.label}
            </button>
          ))}
          <input data-testid="vr-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What they said (optional)"
            className="min-w-[160px] flex-1 rounded-lg border border-brand-line px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          <button data-testid="vr-add-btn" type="submit" disabled={busy || !channel.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
            <Plus size={15} /> Log
          </button>
        </div>
      </form>

      {/* Responses list */}
      {data.responses.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="vr-list">
          {data.responses.map((r) => {
            const meta = INTEREST.find((i) => i.key === r.interested) || INTEREST[0];
            return (
              <div key={r.response_id} className="flex items-center justify-between gap-3 rounded-lg border border-brand-line px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.channel}{r.contact ? ` · ${r.contact}` : ""}</p>
                  {r.note && <p className="truncate text-xs text-slate-500">{r.note}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${meta.cls}`}><meta.icon size={11} /> {meta.label}</span>
                  <button data-testid={`vr-delete-${r.response_id}`} onClick={() => remove(r.response_id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
